import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Building2, Loader2, Sparkles } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { useProDialog } from "@/components/ProDialog";

export const Route = createFileRoute("/dashboard/new-workspace")({
	component: NewWorkspacePage,
});

const pageShellClass =
	"mx-auto grid w-full max-w-none gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8 lg:py-10 xl:max-w-[1800px]";
const sectionCardClass = "bg-card shadow-none";
const sectionHeaderClass =
	"mb-5 flex flex-col gap-2 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between";
const sectionIconClass =
	"flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground";
const sectionTitleClass = "text-base font-semibold tracking-normal";
const sectionDescriptionClass = "text-sm leading-6 text-muted-foreground";

function NewWorkspacePage() {
	const workspaceNameId = useId();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { openProDialog } = useProDialog();
	const [name, setName] = useState("");
	const [isCreating, setIsCreating] = useState(false);

	const handleCreate = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim() || isCreating) return;
		setIsCreating(true);

		try {
			const res = await fetch("/api/workspaces", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({ name: name.trim() }),
			});

			const data = await res.json();

			if (!res.ok) {
				toast.error(data.error || "Failed to create workspace");
				if (data.limitReached) {
					openProDialog();
				}
				return;
			}

			await queryClient.invalidateQueries();
			toast.success("Workspace created");
			navigate({ to: "/dashboard" });
		} catch {
			toast.error("Failed to create workspace");
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<DashboardLayout>
			<div className={pageShellClass}>
				<div className="lg:sticky lg:top-24 lg:self-start">
					<button
						type="button"
						onClick={() => navigate({ to: "/dashboard" })}
						className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<ArrowLeft className="size-4" />
						Back to dashboard
					</button>
					<DashboardPageHeader
						title="Create workspace"
						description="Create a separate operating space for social connections, posts, and MCP access."
					/>
				</div>

				<div className="space-y-4">
					<BrandCard
						variant="default"
						padding="lg"
						className={sectionCardClass}
					>
						<BrandCardHeader className={sectionHeaderClass}>
							<div className="flex items-start gap-3">
								<div className={sectionIconClass}>
									<Building2 className="size-4" />
								</div>
								<div>
									<BrandCardTitle className={sectionTitleClass}>
										Workspace details
									</BrandCardTitle>
									<BrandCardDescription className={sectionDescriptionClass}>
										Name the workspace your team will see in navigation.
									</BrandCardDescription>
								</div>
							</div>
						</BrandCardHeader>
						<BrandCardContent>
							<form onSubmit={handleCreate} className="grid gap-4">
								<BrandInputGroup
									label="Workspace name"
									className="[&_label]:text-sm [&_p]:text-sm"
								>
									<BrandInput
										id={workspaceNameId}
										type="text"
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="Launch team"
										maxLength={80}
										className="h-9 text-sm file:text-sm"
										icon={<Building2 className="size-4" />}
									/>
								</BrandInputGroup>
								<p className="text-xs leading-5 text-muted-foreground">
									You can rename this later from the workspace settings page.
								</p>
								<BrandButton
									type="submit"
									size="sm"
									disabled={!name.trim() || isCreating}
								>
									{isCreating ? (
										<>
											<Loader2 className="size-4 animate-spin" />
											Creating...
										</>
									) : (
										<>
											<Sparkles className="size-4" />
											Create workspace
										</>
									)}
								</BrandButton>
							</form>
						</BrandCardContent>
					</BrandCard>

					<div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm leading-6 text-muted-foreground">
						New workspaces start with their own member list, connected accounts,
						media library, and API access.
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
}
